import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { makeObservable } from 'mobx';
import { Scrollbars } from 'react-custom-scrollbars-4';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { type NoteInfo } from '../../models/NoteInfo';
import { type CategoryInfo } from '../../models/CategoryInfo';
import CategoryPicker, { type CategoryContext } from '../CategoryPicker';
import ContextMenu from '../ContextMenu';
import { ImgGear } from '../Images/ImgGear';
import { ImgLock } from '../Images/ImgLock';
import { ImgShare } from '../Images/ImgShare';
import { ImgArchive } from '../Images/ImgArchive';
import { ImgSearch } from '../Images/ImgSearch';
import { ImgShield } from '../Images/ImgShield';
import { ImgPlusCircle } from '../Images/ImgPlusCircle';
import { ImgContentSearch } from '../Images/ImgContentSearch';
import { themeService } from '../../services/ThemeService';

import './_sidebar.scss';
import { ImgLight } from '@components/Images/ImgLight';
import { ImgDark } from '@components/Images/ImgDark';

export type NoteMatch = {
  noteId: string;
  parts: string[];
};

const untitledNoteTitle = 'Untitled note';

const getNoteStyle = (note: NoteInfo): React.CSSProperties => {
  const color = note.color;
  if (!color || !color.match(/#[0-9A-Fa-f]{6}/g)) {
    return {};
  }
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);
  return {
    borderLeftColor: color,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.08)`
  };
};

/* ----------------------------------------
   SortableItem
   ---------------------------------------- */

interface SortableItemProps {
  note: NoteInfo;
  isDisabled: boolean;
  isActive: boolean;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  matches: NoteMatch[] | null;
}

const SortableItem = React.memo<SortableItemProps>(function SortableItem({
  note, isDisabled, isActive, onMouseDown, matches
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: note.id, disabled: isDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    ...getNoteStyle(note)
  };

  const match = matches?.find(x => x.noteId === note.id);

  let className = 'note-item';
  if (isActive) className += ' active';
  if (note.color && note.color.match(/#[0-9A-Fa-f]{6}/g)) className += ' note-item-colored';

  return (
    <li
      ref={setNodeRef}
      data-id={note.id}
      className={className}
      style={style}
      onMouseDown={(e) => onMouseDown(e, note)}
      {...attributes}
      {...listeners}
    >
      <span className="note-item-icon">
        {note.isPrivate ? <ImgLock size={14} /> : <ImgShare size={14} />}
      </span>
      <span className="note-item-body">
        <span className="note-item-title">{note.name || untitledNoteTitle}</span>
        {match && (
          <span className="note-item-matches">
            {match.parts.map((m, i) => (
              <span key={i} className="search-match" dangerouslySetInnerHTML={{ __html: m }} />
            ))}
          </span>
        )}
      </span>
      {note.isProtected && (
        <span className="note-item-protected"><ImgShield size={14} /></span>
      )}
      <span className="note-item-actions">
        <span className="note-action-btn context-menu-target">
          <ImgGear size={14} />
        </span>
      </span>
    </li>
  );
});

/* ----------------------------------------
   SortableNotesList
   ---------------------------------------- */

interface SortableNotesListProps {
  notes: NoteInfo[];
  filterText: string;
  matches: NoteMatch[] | null;
  isTouchDevice: boolean;
  activeNoteId: string;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SortableNotesList = React.memo<SortableNotesListProps>(function SortableNotesList({
  notes, filterText, matches, isTouchDevice, activeNoteId, onMouseDown, onDragEnd
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
        delay: isTouchDevice ? 400 : 200,
        tolerance: 10
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={notes.map(n => n.id)} strategy={verticalListSortingStrategy}>
        <Scrollbars hideTracksWhenNotNeeded autoHide className="scrollbars-wrapper">
          <ul className="notes-list">
            {notes.map((note) => (
              <SortableItem
                key={note.id}
                note={note}
                isDisabled={!!filterText}
                isActive={note.id === activeNoteId}
                onMouseDown={onMouseDown}
                matches={matches}
              />
            ))}
          </ul>
        </Scrollbars>
      </SortableContext>
    </DndContext>
  );
});

/* ----------------------------------------
   NoteSidebar (exported class component)
   ---------------------------------------- */

export interface NoteSidebarProps {
  notes: NoteInfo[];
  categories: CategoryInfo[];
  filterText: string;
  matches: NoteMatch[] | null;
  isArchive: boolean;
  searchContent: boolean;
  activeCategoryId: string;
  activeNoteId: string;
  isMobileSidebarVisible: boolean;
  userName: string;
  isTouchDevice: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onFilterChange: (text: string) => void;
  onSearchContentToggle: () => void;
  onArchiveToggle: () => void;
  onCategoryChanged: (cat: CategoryInfo) => void;
  onCategorySettingsClick: (context: CategoryContext) => void;
  onNoteMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  onAddNote: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onRenderContextMenu: (element: HTMLElement) => JSX.Element | null;
}

@observer
export default class NoteSidebar extends React.Component<NoteSidebarProps> {
  constructor(props: NoteSidebarProps) {
    super(props);
    makeObservable(this);
  }

  render(): JSX.Element {
    const {
      notes, categories, filterText, matches, isArchive, searchContent,
      activeCategoryId, activeNoteId, isMobileSidebarVisible, userName, isTouchDevice,
      containerRef, onFilterChange, onSearchContentToggle, onArchiveToggle,
      onCategoryChanged, onCategorySettingsClick, onNoteMouseDown, onAddNote,
      onDragEnd, onRenderContextMenu
    } = this.props;

    const noteCountLabel = isArchive ? 'Archive' : 'Notes';
    const noteCountSuffix = notes.length > 0 ? ` · ${notes.length}` : '';

    return (
      <div
        className={`note-list-container${isMobileSidebarVisible ? ' mobile-visible' : ''}`}
        ref={containerRef}
      >
        {/* Search bar */}
        <div className="sidebar-search">
          <span className="sidebar-search-icon">
            <ImgSearch size={16} />
          </span>
          <input
            className="sidebar-search-input"
            placeholder="Search notes..."
            value={filterText}
            onChange={e => onFilterChange(e.target.value)}
          />
          <div className="sidebar-search-actions">
            {!!filterText && (
              <span
                title="Search inside note content"
                className={`sidebar-search-btn${searchContent ? ' active' : ''}`}
                onClick={onSearchContentToggle}
              >
                <ImgContentSearch size={16} />
              </span>
            )}
            <span
              title={isArchive ? 'Showing archived notes' : 'Show archived notes'}
              className={`sidebar-search-btn${isArchive ? ' active danger' : ''}`}
              onClick={onArchiveToggle}
            >
              <ImgArchive size={16} />
            </span>
          </div>
        </div>

        {/* Category picker */}
        {categories.length > 1 && !filterText.startsWith('@') && (
          <CategoryPicker
            categories={categories}
            activeCategoryId={activeCategoryId}
            onRenderItems={(c) => (
              <div className="item item-settings" onClick={() => onCategorySettingsClick(c)}>
                Settings <ImgGear size={14} className="fa-icon" />
              </div>
            )}
            onCategoryChanged={onCategoryChanged}
          />
        )}

        {/* Context menu (scoped to this container) */}
        <ContextMenu
          container={containerRef}
          onAppear={e => e.tagName !== 'INPUT' && !e.closest('.sidebar-footer')}
        >
          {(element) => onRenderContextMenu(element)}
        </ContextMenu>

        {/* Section header */}
        <div className="sidebar-section-header">
          <span className="sidebar-section-label">
            {noteCountLabel}{noteCountSuffix}
          </span>
          <button className="sidebar-add-btn" onClick={onAddNote} title="New note">
            <ImgPlusCircle size={16} />
            <span>New</span>
          </button>
        </div>

        {/* Notes list */}
        <div className="sidebar-notes-section">
          <SortableNotesList
            notes={notes}
            filterText={filterText}
            matches={matches}
            isTouchDevice={isTouchDevice}
            activeNoteId={activeNoteId}
            onMouseDown={onNoteMouseDown}
            onDragEnd={onDragEnd}
          />
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <span className="sidebar-footer-user" title={userName}>{userName}</span>
          <span
            className="sidebar-footer-theme"
            onClick={themeService.toggle}
            title={themeService.isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {themeService.isDark ? <ImgLight size={16} /> : <ImgDark size={16} />}
          </span>
        </div>
      </div>
    );
  }
}
